import { Divider } from "$root/components/Divider";
import { handleSubmitToCustomSubmitHandler } from "$root/third-party-integrations/react-hook-form";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { zodResolver } from "@hookform/resolvers/zod";
import { Column } from "@mdotm/shadcn/legacy/components";
import { forwardRef, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { WorkspaceChartChain } from "../chain";
import { autoGenerateMetricName } from "../common";
import { type ConceptFormFields, conceptFormModel, conceptFormSubmitSchema } from "../concept-form";
import type { SubmittableHandle } from "./type";

/**
 * A new column is a parameterized concept and nothing else, so the panel's form model *is*
 * {@link conceptFormModel} — the alias is kept because the panel's props are named after it.
 */
export type WorkspaceCreateTableSettingForm = ConceptFormFields;

/**
 * What a new column has to answer at submit time. Built from the model rather than from
 * `workspaceChainSchema`, which declared no `autoGenerateName` and so deleted it on the way to submit.
 */
export const workspaceCreateTableSettingResolverSchema = conceptFormSubmitSchema(conceptFormModel);

const WorkspaceTableSettingsColumnAdd = forwardRef<
	SubmittableHandle,
	{ onSubmit(values: WorkspaceCreateTableSettingForm): void }
>(function AddColumn({ onSubmit }, ref) {
	const form = useForm<WorkspaceCreateTableSettingForm>({
		defaultValues: { autoGenerateName: true },
		resolver: zodResolver(workspaceCreateTableSettingResolverSchema),
	});

	useImperativeHandle(ref, () => ({
		submit: () =>
			handleSubmitToCustomSubmitHandler({
				handleSubmit: form.handleSubmit,
				baseSubmitFn: onSubmit,
			})(),
	}));

	return (
		<Column gap={16}>
			<div className="grid grid-cols-2 grid-rows-1 gap-x-4 gap-y-2 items-end ">
				<WorkspaceChartChain
					autogenerateName={form.watch("autoGenerateName")}
					onChange={(newWorkspaceChain) => {
						form.reset({
							...newWorkspaceChain,
							autoGenerateName: form.getValues("autoGenerateName"),
						});
					}}
				/>
			</div>
			<Divider />
			<Column gap={8}>
				<FormFields.Text
					name="name"
					control={form.control}
					formState={form.formState}
					label="Name"
					disabled={form.watch("autoGenerateName")}
				/>
				<FormFields.Checkbox
					name="autoGenerateName"
					control={form.control}
					formState={form.formState}
					className="flex-row-reverse gap-2 w-fit"
					onChange={(shouldAutogenerate) => {
						if (shouldAutogenerate) {
							form.reset({
								...form.getValues(),
								name: autoGenerateMetricName(form.getValues()),
							});
						}
					}}
				>
					Auto-generated
				</FormFields.Checkbox>
			</Column>
			{/* <FormFields.TextArea
							name="description"
							control={form.control}
							formState={form.formState}
							label="Description"
							placeholder="Add a free description to this item. The text will be displayed in the lis"
						/> */}
		</Column>
	);
});

export default WorkspaceTableSettingsColumnAdd;
